/**
 * The Ulule categories this page links to and filters on.
 *
 * `data.ulule.com/stats.json` gives each category an id and its labels but no slug, the canonical
 * category page is `/categories/<slug>/`, and the project search filters on `tag_id:<id>` — so the
 * three have to be paired somewhere. The site's own list carries all three, but it reaches the
 * discover page as `DISCOVER_DATA`, which a prerendered page cannot read; it is mirrored here, in
 * the same order the discover filter uses (alphabetical by label).
 *
 * Ids are stable and the set changes about once a year, so this is a deliberate trade: a category
 * added to the feed stays out of the Top 10 filter, and renders as a plain ledger row, until it is
 * added here — rather than linking somewhere that does not exist.
 */
export type Category = {
  /** Feed id, and the `tag_id:` the project search filters on. */
  id: number
  /** Last segment of the category page URL; also the `?cat=` value and the Plausible prop. */
  slug: string
  /** French label, as both the site and the stats feed spell it. */
  label: string
}

export const ULULE_CATEGORIES: readonly Category[] = [
  { id: 41, slug: 'art-photo', label: 'Art & Photo' },
  { id: 40, slug: 'craft-food', label: 'Artisanat & Cuisine' },
  { id: 24, slug: 'other', label: 'Autres projets' },
  { id: 7, slug: 'bandes-dessinees', label: 'BD & Manga' },
  { id: 354, slug: 'creation-de-contenus', label: 'Création de contenus' },
  { id: 45, slug: 'childhood-educ', label: 'Enfance & Educ.' },
  { id: 4, slug: 'movies', label: 'Film & vidéo' },
  { id: 10, slug: 'games', label: 'Jeux' },
  { id: 56, slug: 'spaces', label: 'Lieux & Commerces' },
  { id: 39, slug: 'fashion-design', label: 'Mode & Design' },
  { id: 3, slug: 'music', label: 'Musique' },
  { id: 37, slug: 'heritage', label: 'Patrimoine' },
  { id: 63, slug: 'sante-bien-etre', label: 'Santé & Bien-être' },
  { id: 44, slug: 'charities-citizen', label: 'Solidaire & Citoyen' },
  { id: 20, slug: 'performing-arts', label: 'Spectacle vivant' },
  { id: 21, slug: 'sport', label: 'Sports' },
  { id: 43, slug: 'technology', label: 'Technologie' },
  { id: 42, slug: 'publishing-journalism', label: 'Édition & Média' },
]

/** The category's page on the site. */
export function categoryHref(slug: string): string {
  return `/categories/${slug}/`
}

/** Lookup for the ledger rows, which arrive from the feed knowing an id and nothing else. */
export const CATEGORY_BY_ID: Readonly<Record<number, Category>> = Object.fromEntries(
  ULULE_CATEGORIES.map((category) => [category.id, category]),
)
